'use client';

import { ConsentState, useCookieConsent } from '@/app/contexts/cookie-consent-context';
import { Box, Button, IconButton, Link, Modal, styled, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useIsDesktop from "@/hooks/useDesktop";

export default function ConsentManager() {
  const { 
    consent, 
    setConsent, 
    showConsentManager, 
    setShowConsentManager 
  } = useCookieConsent();

  const handleToggleConsent = (category: keyof ConsentState) => {
    // Don't allow toggling necessary cookies
    if (category === 'necessary') return;

    setConsent({
      ...consent,
      [category]: !consent[category],
    });
  };

  const handleSave = () => {
    setShowConsentManager(false);
  };

  // TODO: Redundant functionality with ConsentBanner's handleAcceptAll
  // Both methods set the same consent state. Consider extracting this to a shared utility function
  // that can be imported by both ui to ensure consistent behavior.
  // See the related TODO in consent-banner.tsx
  const handleAcceptAll = () => {
    setConsent({
      necessary: true,
      analytics: true,
      externalMedia: true,
    });
    setShowConsentManager(false);
  };

  const handleAcceptEssential = () => {
    setConsent({
      necessary: true,
      analytics: false,
      externalMedia: false,
    });
    setShowConsentManager(false);
  };

  return (
    <Modal
      open={showConsentManager}
      onClose={() => setShowConsentManager(false)}
      aria-labelledby="cookie-settings-modal"
      aria-describedby="manage-cookie-preferences"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999, // Ensure it's always on top
      }}
    >
      <Box
        sx={{
            backgroundColor: 'var(--background)',
            color: 'var(--foreground)',
            padding: (theme) => theme.spacing(3),
            borderRadius: 1,
            maxWidth: { xs: '90vw', md: '60vw' },
            width: '100%',
            maxHeight: { xs: '95vh', md: '90vh' },
            overflow: 'auto',
            outline: 'none',
            boxShadow: (theme) => theme.shadows[5],
        }}
      >
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: (theme) => theme.spacing(2),
            }}
        >
          <Typography variant="h2" component="h2" sx={{ color: 'var(--foreground)' }}>
            Cookie Einstellungen
          </Typography>
          <IconButton
              aria-label="close-cookie-banner"
            onClick={() => setShowConsentManager(false)}
            size="small"
            sx={{ color: 'var(--foreground)' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="body1" sx={{ mb: 3, color: 'var(--foreground)' }}>
          Wenn Sie unter 16 Jahre alt sind und Ihre Zustimmung zu freiwilligen Diensten geben möchten, müssen Sie Ihre Erziehungsberechtigten um Erlaubnis bitten.
          Wir verwenden Cookies und andere Technologien auf unserer Website. Einige von ihnen sind essenziell, während andere uns helfen, diese Website und Ihre Erfahrung zu verbessern. Personenbezogene Daten können verarbeitet werden (z. B. IP-Adressen), z. B. für personalisierte Anzeigen und Inhalte oder Anzeigen- und Inhaltsmessung. Weitere Informationen über die Verwendung Ihrer Daten finden Sie in unserer <Link href="/privacy">Datenschutzerklärung</Link>.
        </Typography>

        <Typography variant="body1" sx={{ mb: 3, color: 'var(--foreground)' }}>
          Hier finden Sie eine Übersicht über alle verwendeten Cookies. Sie können Ihre Einwilligung zu ganzen Kategorien geben oder sich weitere Informationen anzeigen lassen und so nur bestimmte Cookies auswählen.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          {/* Necessary Cookies - Always enabled */}
          <CookieOption 
            sx={{ backgroundColor: 'var(--secondary)', border: '1px solid var(--foreground)' }}
          >
            <Box sx={{ mr: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'var(--foreground)' }}>
                Essenzielle Cookies
              </Typography>
              <Typography variant="caption" sx={{ color: 'var(--foreground)' }}>
                Essenzielle Cookies ermöglichen grundlegende Funktionen und sind für die einwandfreie Funktion der Website erforderlich.
              </Typography>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: { xs: 'column-reverse', md: 'row' }
                }}
            >
              <Typography variant="caption" sx={{ mr: 1, mt: 1, color: 'var(--foreground)' }}>
                Immer Aktiv
              </Typography>
              <ToggleSwitch active={true} />
            </Box>
          </CookieOption>

          {/* Analytics Cookies */}
          <CookieOption 
            sx={{ backgroundColor: 'var(--secondary)', border: '1px solid var(--foreground)' }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'var(--foreground)' }}>
                Analyse Cookies
              </Typography>
              <Typography variant="caption" sx={{ color: 'var(--foreground)' }}>
                Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren.
              </Typography>
            </Box>
            <Box>
              <Button 
                onClick={() => handleToggleConsent('analytics')}
                aria-label={consent.analytics ? 'Analyse-Cookies deaktivieren' : 'Analyse-Cookies aktivieren'}
                aria-pressed={consent.analytics}
                sx={{ minWidth: 'auto', p: 0 }}
              >
                <ToggleSwitch active={consent.analytics} />
              </Button>
            </Box>
          </CookieOption>

          {/* Maps Cookies */}
          <CookieOption 
            sx={{ backgroundColor: 'var(--secondary)', border: '1px solid var(--foreground)' }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'var(--foreground)' }}>
                Externe Medien
              </Typography>
              <Typography variant="caption" sx={{ color: 'var(--foreground)' }}>
                <p>Inhalte von Videoplattformen und Social-Media-Plattformen werden standardmäßig blockiert. Wenn Cookies von externen Medien akzeptiert werden, bedarf der Zugriff auf diese Inhalte keiner manuellen Einwilligung mehr.</p>
                <p>Einige Services verarbeiten personenbezogene Daten in den USA. Mit Ihrer Einwilligung zur Nutzung dieser Services stimmen Sie auch der Verarbeitung Ihrer Daten in den USA gemäß Art. 49 (1) lit. a DSGVO zu. Der EuGH stuft die USA als Land mit unzureichendem Datenschutz nach EU-Standards ein. So besteht etwa das Risiko, dass US-Behörden personenbezogene Daten in Überwachungsprogrammen verarbeiten, ohne bestehende Klagemöglichkeit für Europäer.</p>
              </Typography>
            </Box>
            <Box>
              <Button 
                onClick={() => handleToggleConsent('externalMedia')}
                aria-label={consent.externalMedia ? 'Externe Medien deaktivieren' : 'Externe Medien aktivieren'}
                aria-pressed={consent.externalMedia}
                sx={{ minWidth: 'auto', p: 0 }}
              >
                <ToggleSwitch active={consent.externalMedia} />
              </Button>
            </Box>
          </CookieOption>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexDirection: { xs: 'column', md: 'row' }, gap: 1 }}>
          <Button
            onClick={handleSave}
            variant="outlined"
            sx={{ 
              backgroundColor: 'var(--secondary)', 
              color: 'var(--foreground)',
              border: '1px solid var(--foreground)',
              '&:hover': {
                backgroundColor: 'var(--secondary)',
                opacity: 0.9,
              }
            }}
          >
            Speichern
          </Button>
          <Button
              onClick={handleAcceptEssential}
              variant="outlined"
              sx={{
                backgroundColor: 'var(--secondary)',
                color: 'var(--foreground)',
                border: '1px solid var(--foreground)',
                '&:hover': {
                  backgroundColor: 'var(--secondary)',
                  opacity: 0.9,
                }
              }}
          >
            Nur essenzielle Cookies akzeptieren
          </Button>
          <Button
            onClick={handleAcceptAll}
            variant="contained"
            sx={{ 
              backgroundColor: 'var(--primary)', 
              color: 'primary.contrastText',
              '&:hover': {
                backgroundColor: 'var(--primary)',
                opacity: 0.9,
              }
            }}
          >
            Alle akzeptieren
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

const CookieOption = styled(Box) (({ theme }) => {
    const isDesktop = useIsDesktop();

    return {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: isDesktop ? 'center' : 'flex-start',
        // flexDirection: isDesktop ? 'row' : 'column',
        padding: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
    }
});

const ToggleSwitch = ({ active }: { active: boolean }) => (
  <Box 
    sx={{ 
      width: 40, 
      height: 20, 
      borderRadius: 10, 
      position: 'relative',
      backgroundColor: active ? 'var(--primary)' : 'var(--foreground)',
      opacity: active ? 1 : 0.5,
      transition: 'background-color 0.3s',
    }}
  >
    <Box 
      sx={{ 
        position: 'absolute', 
        top: 2, 
        left: active ? 'auto' : 2, 
        right: active ? 2 : 'auto',
        width: 16, 
        height: 16, 
        borderRadius: '50%', 
        backgroundColor: '#fff',
        transition: 'left 0.3s, right 0.3s',
      }} 
    />
  </Box>
);
